import { Component, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProductService } from '../../services/product.service';
import { SettingsService } from '../../services/settings.service';
import { AuthService } from '../../services/auth.service';
import { CartService } from '../../services/cart.service';
import { WishlistService } from '../../services/wishlist.service';
import { ProductDto } from '../../models/product.dto';
import { ProductRatingComponent } from '../product-rating/product-rating.component';

@Component({
  selector: 'app-product-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ButtonModule,
    ProgressSpinnerModule,
    ToastModule,
    ProductRatingComponent
  ],
  providers: [MessageService],
  templateUrl: './product-detail.component.html',
  styleUrl: './product-detail.component.css'
})
export class ProductDetailComponent implements OnInit {
  product = signal<ProductDto | null>(null);
  loading = signal(true);
  error = signal<string | null>(null);
  
  // Route params
  platformId = signal<string>('');
  platformName = signal<string>('');
  productId = signal<string>('');
  
  // Image gallery
  selectedImageIndex = signal(0);
  productImages = signal<string[]>([]);
  
  // Quantity selector
  quantity = signal(1);
  
  // Related products
  relatedProducts = signal<ProductDto[]>([]);
  loadingRelated = signal(false);
  
  // Loading states
  addingToCart = signal(false);
  addingToWishlist = signal(false);
  isInWishlist = signal(false);
  checkingWishlist = signal(false);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private settingsService: SettingsService,
    private authService: AuthService,
    private cartService: CartService,
    private wishlistService: WishlistService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const platformId = params['id'];
      const platformName = params['name'] ? decodeURIComponent(params['name']) : '';
      const productId = params['productId'];
      
      if (platformId && productId) {
        this.platformId.set(platformId);
        this.platformName.set(platformName);
        this.productId.set(productId);
        this.loadProduct();
      } else {
        this.error.set('Invalid route parameters');
        this.loading.set(false);
      }
    });
  }

  private loadProduct(): void {
    const productId = this.productId();
    const platformId = this.platformId();
    
    if (!productId || !platformId) {
      this.error.set('Product ID or Platform ID not found');
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const environment = this.settingsService.getEnvironment();

    this.productService.getProductById(productId, platformId, environment).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.product.set(response.data);
          this.parseProductImages(response.data);
          this.loadRelatedProducts();
          this.checkWishlistStatus();
        } else {
          this.error.set(response.message || 'Product not found');
        }
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading product:', error);
        this.error.set('Failed to load product details');
        this.loading.set(false);
      }
    });
  }

  private parseProductImages(product: ProductDto): void {
    if (product.images) {
      try {
        // Check if images is already an array (from JSON deserialization)
        let images: string[];
        if (typeof product.images === 'string') {
          // Try to parse as JSON array
          const parsed = JSON.parse(product.images);
          if (Array.isArray(parsed)) {
            images = parsed;
          } else if (typeof parsed === 'string') {
            images = [parsed];
          } else {
            images = [product.images];
          }
        } else if (Array.isArray(product.images)) {
          // Already an array (from JSON deserialization)
          images = product.images;
        } else {
          // Fallback to single image string
          images = [product.images as any];
        }
        this.productImages.set(images);
      } catch {
        // If parsing fails, treat as single image string
        if (typeof product.images === 'string' && product.images.trim()) {
          this.productImages.set([product.images]);
        } else {
          this.productImages.set([]);
        }
      }
    } else {
      this.productImages.set([]);
    }
  }

  private loadRelatedProducts(): void {
    const product = this.product();
    if (!product) return;

    this.loadingRelated.set(true);
    const platformId = this.platformId();
    const environment = this.settingsService.getEnvironment();

    // Load related products from the same category
    this.productService.getProducts(platformId, environment, {
      categoryId: product.productCategoryId || undefined,
      pageSize: 4
    }).subscribe({
      next: (response) => {
        if (response.success) {
          // Filter out the current product and limit to 4
          const related = response.data
            .filter(p => p.id !== product.id)
            .slice(0, 4);
          this.relatedProducts.set(related);
        }
        this.loadingRelated.set(false);
      },
      error: (error) => {
        console.error('Error loading related products:', error);
        this.loadingRelated.set(false);
      }
    });
  }

  // Image gallery methods
  selectImage(index: number): void {
    this.selectedImageIndex.set(index);
  }

  nextImage(): void {
    const images = this.productImages();
    if (images.length > 0) {
      const currentIndex = this.selectedImageIndex();
      this.selectedImageIndex.set((currentIndex + 1) % images.length);
    }
  }

  previousImage(): void {
    const images = this.productImages();
    if (images.length > 0) {
      const currentIndex = this.selectedImageIndex();
      this.selectedImageIndex.set(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
    }
  }

  getCurrentImage(): string {
    const images = this.productImages();
    const index = this.selectedImageIndex();
    if (images.length > index) {
      return images[index];
    }
    return '/assets/placeholder-product.png';
  }

  // Quantity methods
  increaseQuantity(): void {
    const product = this.product();
    if (product && this.quantity() < product.stockQuantity) {
      this.quantity.set(this.quantity() + 1);
    }
  }

  decreaseQuantity(): void {
    if (this.quantity() > 1) {
      this.quantity.set(this.quantity() - 1);
    }
  }

  onQuantityChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value, 10);
    const product = this.product();
    
    if (value >= 1 && product && value <= product.stockQuantity) {
      this.quantity.set(value);
    } else {
      target.value = this.quantity().toString();
    }
  }

  // Action methods
  addToCart(): void {
    const product = this.product();
    if (!product || product.stockQuantity === 0) return;

    if (!this.authService.isAuthenticated()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Authentication Required',
        detail: 'Please sign in to add items to your cart',
        life: 3000
      });
      setTimeout(() => {
        this.router.navigate(['/signin'], { 
          queryParams: { returnUrl: this.router.url } 
        });
      }, 1500);
      return;
    }

    this.addingToCart.set(true);
    const platformId = this.platformId();
    const quantity = this.quantity();

    this.cartService.addToCart({
      productId: product.id,
      quantity: quantity
    }, platformId).subscribe({
      next: (response) => {
        this.addingToCart.set(false);
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Added to Cart!',
            detail: `Added ${quantity} x ${product.name} to your cart`,
            life: 4000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to Add Item',
            detail: response.message || 'Failed to add item to cart. Please try again.',
            life: 5000
          });
        }
      },
      error: (error) => {
        this.addingToCart.set(false);
        console.error('Error adding to cart:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Connection Error',
          detail: 'Error adding item to cart. Please check your connection and try again.',
          life: 5000
        });
      }
    });
  }

  shareProduct(): void {
    if (navigator.share) {
      const product = this.product();
      navigator.share({
        title: product?.name,
        text: product?.description || '',
        url: window.location.href
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Link Copied!',
          detail: 'Product link has been copied to your clipboard',
          life: 3000
        });
      }).catch(() => {
        this.messageService.add({
          severity: 'error',
          summary: 'Copy Failed',
          detail: 'Failed to copy link to clipboard',
          life: 3000
        });
      });
    }
  }

  // Navigation methods
  goBack(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    if (platformId && platformName) {
      this.router.navigate(['/store', platformId, encodeURIComponent(platformName)]);
    } else {
      this.router.navigate(['/']);
    }
  }

  navigateToProduct(productId: string): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    if (platformId && platformName) {
      this.router.navigate(['/store', platformId, encodeURIComponent(platformName), 'product', productId]);
    }
  }

  // Utility methods
  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(price);
  }

  getDiscountPercentage(): number | null {
    const product = this.product();
    if (product && product.compareAtPrice && product.compareAtPrice > product.price) {
      return Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100);
    }
    return null;
  }

  getStockStatus(): string {
    const product = this.product();
    if (!product) return '';
    
    if (product.stockQuantity === 0) {
      return 'Out of Stock';
    } else if (product.stockQuantity <= 10) {
      return `Only ${product.stockQuantity} left in stock`;
    } else {
      return 'In Stock';
    }
  }

  getStockStatusClass(): string {
    const product = this.product();
    if (!product) return '';
    
    if (product.stockQuantity === 0) {
      return 'out-of-stock';
    } else if (product.stockQuantity <= 10) {
      return 'low-stock';
    } else {
      return 'in-stock';
    }
  }

  getDeliveryEstimate(): string {
    const product = this.product();
    if (!product) return '';
    
    if (product.minDeliveryDays && product.maxDeliveryDays) {
      return `${product.minDeliveryDays}-${product.maxDeliveryDays} days`;
    } else if (product.minDeliveryDays) {
      return `${product.minDeliveryDays}+ days`;
    } else {
      return 'Standard delivery';
    }
  }

  getInstallationInfo(): string {
    const product = this.product();
    if (!product) return '';
    
    if (product.installationAvailable) {
      const charge = product.installationCharge ? 
        ` (${this.formatPrice(product.installationCharge)} charge)` : 
        ' (Free)';
      return `Installation available${charge}`;
    }
    return 'Self-installation required';
  }

  // Utility methods for template
  get Math() {
    return Math;
  }

  // Scroll to reviews section
  scrollToReviews(): void {
    const reviewsElement = document.getElementById('reviews');
    if (reviewsElement) {
      reviewsElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }

  // Wishlist methods
  private checkWishlistStatus(): void {
    const product = this.product();
    const platformId = this.platformId();
    
    if (!product || !this.authService.isAuthenticated() || !platformId) {
      this.isInWishlist.set(false);
      return;
    }

    this.checkingWishlist.set(true);
    this.wishlistService.isProductInWishList(product.id, platformId).subscribe({
      next: (response) => {
        this.isInWishlist.set(response.success ? (response.data || false) : false);
        this.checkingWishlist.set(false);
      },
      error: (error) => {
        console.error('Error checking wishlist status:', error);
        this.isInWishlist.set(false);
        this.checkingWishlist.set(false);
      }
    });
  }

  toggleWishlist(): void {
    const product = this.product();
    const platformId = this.platformId();
    
    if (!product || !this.authService.isAuthenticated()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Authentication Required',
        detail: 'Please login to add items to your wishlist',
        life: 3000
      });
      return;
    }

    if (!platformId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Platform ID not found',
        life: 3000
      });
      return;
    }

    if (this.addingToWishlist()) return;

    if (this.isInWishlist()) {
      this.removeFromWishlist();
    } else {
      this.addToWishlist();
    }
  }

  private addToWishlist(): void {
    const product = this.product();
    
    if (!product) return;

    this.addingToWishlist.set(true);

    this.wishlistService.addToWishList({
      productId: product.id,
      priority: 1
    }).subscribe({
      next: (response) => {
        if (response.success) {
          this.isInWishlist.set(true);
          this.messageService.add({
            severity: 'success',
            summary: 'Added to Wishlist',
            detail: 'Product added to wishlist successfully',
            life: 3000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to Add',
            detail: response.message || 'Failed to add to wishlist',
            life: 3000
          });
        }
        this.addingToWishlist.set(false);
      },
      error: (error) => {
        console.error('Error adding to wishlist:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to Add',
          detail: 'Failed to add to wishlist',
          life: 3000
        });
        this.addingToWishlist.set(false);
      }
    });
  }

  private removeFromWishlist(): void {
    const product = this.product();
    const platformId = this.platformId();
    
    if (!product || !platformId) return;

    this.addingToWishlist.set(true);
    
    // Get wishlist to find the item ID
    this.wishlistService.getWishList(platformId, 1, 100).subscribe({
      next: (response) => {
        if (response.success && response.data && Array.isArray(response.data)) {
          const wishlistItem = response.data.find((item: any) => item.productId === product.id);
          if (wishlistItem) {
            this.wishlistService.removeFromWishList(wishlistItem.id).subscribe({
              next: (removeResponse) => {
                if (removeResponse.success) {
                  this.isInWishlist.set(false);
                  this.messageService.add({
                    severity: 'success',
                    summary: 'Removed from Wishlist',
                    detail: 'Product removed from wishlist successfully',
                    life: 3000
                  });
                } else {
                  this.messageService.add({
                    severity: 'error',
                    summary: 'Failed to Remove',
                    detail: removeResponse.message || 'Failed to remove from wishlist',
                    life: 3000
                  });
                }
                this.addingToWishlist.set(false);
              },
              error: (error) => {
                console.error('Error removing from wishlist:', error);
                this.messageService.add({
                  severity: 'error',
                  summary: 'Failed to Remove',
                  detail: 'Failed to remove from wishlist',
                  life: 3000
                });
                this.addingToWishlist.set(false);
              }
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Item Not Found',
              detail: 'Item not found in wishlist',
              life: 3000
            });
            this.addingToWishlist.set(false);
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to Load',
            detail: 'Failed to load wishlist',
            life: 3000
          });
          this.addingToWishlist.set(false);
        }
      },
      error: (error) => {
        console.error('Error loading wishlist:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to Load',
          detail: 'Failed to load wishlist',
          life: 3000
        });
        this.addingToWishlist.set(false);
      }
    });
  }

  getRelatedProductImage(product: ProductDto): string {
    if (product.images) {
      try {
        let images: string[];
        if (typeof product.images === 'string') {
          // Try to parse as JSON array
          const parsed = JSON.parse(product.images);
          if (Array.isArray(parsed)) {
            images = parsed;
          } else if (typeof parsed === 'string') {
            images = [parsed];
          } else {
            images = [product.images];
          }
        } else if (Array.isArray(product.images)) {
          // Already an array (from JSON deserialization)
          images = product.images;
        } else {
          // Fallback to empty array
          images = [];
        }
        
        if (images.length > 0) {
          return images[0];
        }
      } catch {
        // If parsing fails, treat as single image string
        if (typeof product.images === 'string' && product.images.trim()) {
          return product.images;
        }
      }
    }
    return '/assets/placeholder-product.png';
  }
}
